/**
 * Fredhopper subscriber for the generic tracking emitter.
 *
 * Maps vendor-agnostic business events to Fredhopper activity events.
 * Add new event mappings here as tracking requirements are defined.
 *
 * IMPORTANT: This module subscribes eagerly at import time (bottom of file),
 * for the same reason as the Bloomreach subscriber: SSR-hydrated components
 * (lister, PDP) emit events before the Bootstrap component mounts and calls
 * initFredhopper(). Events emitted before init are queued in fredhopperClient
 * and flushed once the SDK is initialized.
 */
import { emitter, EmitterEvent } from '../emitter';
import { FredhopperActivityAction } from './constants';
import { trackFredhopper } from './fredhopperClient';
// Partial, not a full Record — a future event added for another vendor's exclusive
// use shouldn't force every existing subscriber to handle it.
const EVENT_MAP = {
    [EmitterEvent.VIEW_ITEM]: data => {
        trackFredhopper(FredhopperActivityAction.VIEW, { product: String(data.product_id) }, undefined, data.source_id);
    },
    [EmitterEvent.CATEGORY_VIEW]: data => {
        // pathToPage must be the actual page path, not a display label — data.category_name
        // (from buildCategoryViewPayload) is a human-readable joined string built for
        // Bloomreach's category dimension and isn't a valid Fredhopper target.
        const searchQuery = data.search_query;
        trackFredhopper(FredhopperActivityAction.VIEW, { pathToPage: window.location.pathname }, searchQuery ? { query: searchQuery } : undefined, data.source_id);
    },
    [EmitterEvent.CART_UPDATE]: data => {
        // A -1 quantity decrease also emits action: 'remove' on the shared payload (see
        // CartUpdateTrackingSaga) — Fredhopper's remove-from-cart must fire only on a full
        // removal, per the docs ("not for quantity adjustments"). Decreases aren't tracked
        // as add-to-cart either — they're covered by the eventual purchase/remove event.
        if (data.action === 'remove') {
            if (data.is_full_removal === false) {
                return;
            }
            // No sourceId here — the docs are explicit that remove-from-cart doesn't need one
            // (it's a basket action, never itself the result of an FHR/XO query response).
            trackFredhopper(FredhopperActivityAction.REMOVE_FROM_CART, { product: String(data.product_id) });
            return;
        }
        trackFredhopper(FredhopperActivityAction.ADD_TO_CART, { product: String(data.product_id) }, { quantity: data.quantity, variant: data.sku_id }, data.source_id);
    },
    [EmitterEvent.ADDITIONAL_PRODUCT_CART_ADD]: data => {
        trackFredhopper(FredhopperActivityAction.ADD_TO_CART, { product: String(data.product_id) }, {
            quantity: data.quantity,
            variant: data.sku_id,
        });
    },
    [EmitterEvent.PRODUCT_CLICK]: data => {
        const target = { product: String(data.product_id) };
        if (data.campaign_id) {
            target.campaign = String(data.campaign_id);
        }
        trackFredhopper(FredhopperActivityAction.CLICK, target, undefined, data.source_id);
    },
    [EmitterEvent.FACET_CLICK]: data => {
        const facetId = String(data.facet_id);
        trackFredhopper(FredhopperActivityAction.CLICK, { facet: facetId, [facetId]: String(data.facet_value) }, undefined, data.source_id);
    },
    [EmitterEvent.PURCHASE]: data => {
        trackFredhopper(FredhopperActivityAction.PURCHASE, { product: String(data.product_id) }, {
            orderId: data.order_id,
            price: data.price,
            quantity: data.quantity,
            currency: data.currency,
        });
    },
    [EmitterEvent.LOGOUT]: () => {
        // No dedicated identity to clear yet — Fredhopper sessions key off the SDK's own
        // attraqtsessionid, and the login identity isn't persisted across page loads anyway
        // (see fredhopperClient.identifyFredhopper), so there's nothing to actively clear here.
    },
};
let subscribed = false;
/** Subscribes to the generic tracking emitter. */
export const subscribeFredhopperToTracking = () => {
    if (subscribed) {
        return;
    }
    subscribed = true;
    Object.entries(EVENT_MAP).forEach(([event, handler]) => {
        emitter.on(event, handler);
    });
};
// Subscribe eagerly so SSR-hydrated components (lister, PDP) can emit events
// before Bootstrap mounts. Events are queued in fredhopperClient until init.
subscribeFredhopperToTracking();
/** Unsubscribes from the generic tracking emitter. */
export const unsubscribeFredhopperFromTracking = () => {
    Object.entries(EVENT_MAP).forEach(([event, handler]) => {
        emitter.off(event, handler);
    });
    subscribed = false;
};
